/*
 * Copyright (c) 2020 Staffino, s.r.o. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Created by Martin Komara, April 2020
 */

import { useCallback, useEffect, useReducer, useRef } from 'react';
import { FlowObject } from './flow-bridge';
import useBridge from './use-bridge-hook';

type NarrowObject<T, N> = T extends { objectType: N } ? T : never;

export default function useObjects<
  T extends FlowObject,
  N extends FlowObject['objectType'],
>(type: N, condition: Partial<FlowObject> = {}): Array<NarrowObject<T, N>> {
  const bridge = useBridge();
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const requestRef = useRef<Promise<T[]>>(undefined);
  const resultRef = useRef<T[]>(undefined);
  const update = useCallback(() => {
    requestRef.current = undefined;
    forceUpdate();
  }, [requestRef, forceUpdate]);
  useEffect(() => {
    bridge.on('update', update);
    return () => {
      bridge.off('update', update);
    };
  });
  if (!requestRef.current) {
    requestRef.current = Promise.resolve(
      bridge.load({ ...condition, objectType: type as any }) as Promise<T[]>,
    )
      .then((result) => {
        if (result !== resultRef.current) {
          resultRef.current = result;
          forceUpdate();
        }
        return result;
      })
      .catch((error) => {
        throw error;
      });
  }
  return (resultRef.current ?? []) as Array<NarrowObject<T, N>>;
}
